import Phaser from 'phaser';

import { BlocksMainScene } from '../../scenes/blocks-main-scene';
import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { GridViewModel } from './grid-view-model';
import { CellView } from './cell-view';
import { Subscription } from 'rxjs';
import { DragData } from '../shape/shape-view-model';
import { TextResizer } from './text-resizer';

export class GridView extends Phaser.GameObjects.Container {
  private subscriptionGroup: Subscription;
  private textResizer: TextResizer;

  private enableAnimations: boolean;
  private cellViews: CellView[] = [];
  private currentScaleFactor = 1;
  private scaledOffsetX = 0;

  constructor(
    scene: BlocksMainScene,
    x: number,
    y: number,
    private viewModel: GridViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();
    this.textResizer = new TextResizer(scene, this, {
      fontFamily: viewModel.defaultFontFamily,
      fontSize: '150px',
      fontStyle: 'bold',
      color: '#ffffff',
    });

    this.enableAnimations = false;

    this.setName(GridView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.onResetGameScene$.subscribe(() => {
        this.cellViews.forEach((view) => {
          view.destroy();
        });
        this.cellViews.length = 0;
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.blocksLogicActionBus$.subscribe((actionBus) => {
        if (!actionBus) {
          return;
        }
        actionBus.on('onAfterShapePlaced', () => {
          this.viewModel.refreshShapeProjections();
        });
        actionBus.on('onShapePointsAdded', (payload) => {
          if (!this.enableAnimations) {
            return;
          }

          if (payload.gridElementsToCollect.length > 0) {
            const result = payload.gridElementsToCollect.reduce<{ row: number; column: number }[]>(
              (acc, gridElement) => {
                const cells = gridElement.cells.map((cell) => ({
                  row: cell.row,
                  column: cell.column,
                }));
                return acc.concat(cells);
              },
              [],
            );
            const rowAve = calcAverage(result.map((val) => val.row));
            const colAve = calcAverage(result.map((val) => val.column));
            this.createPointsLabel(payload.points, colAve + 1, rowAve + 1);
            return;
          }

          const centerCoords = payload.shape.centerGridCoordinates(payload.cellId);
          this.createPointsLabel(payload.points, centerCoords.column + 1, centerCoords.row + 1);
        });
        actionBus.on('onWinCellCollected', (payload) => {
          if (!this.enableAnimations) {
            return;
          }
          const cellView = this.cellViews.find((view) => view.id === payload.cell.id);
          if (cellView) {
            cellView.animateWinCellCollected(payload.displayIndex);
          }
          this.createPointsLabel(
            payload.points,
            payload.cell.column + 1,
            payload.cell.row + 1,
            true,
          );
        });
      }),
    );
    this.subscriptionGroup.add(
      viewModel.onGameSetupStartTrigger.subscribe((_value) => {
        if (viewModel.spawnGrid()) {
          this.cellViews = viewModel.allCellViewModels.map((vm) => new CellView(scene, 0, 0, vm));
        }
        this.cellViews.forEach((view) => view.onResize(scene.currentGameSizeMetrics));
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onGameSetupCompleteTrigger.subscribe(() => {
        this.enableAnimations = true;
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  public handleShapeDrag(dragData: DragData) {
    this.viewModel.handleShapeDrag(dragData, this.cellViews);
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    this.cellViews.forEach((view) => view.onResize(gameSizeMetrics));

    const calc = this.viewModel.calculateScaleFactor(gameSizeMetrics);
    this.currentScaleFactor = calc.scaleFactor;
    this.scaledOffsetX = calc.scaledOffsetX;
  }

  private createPointsLabel(score: number, x: number, y: number, isBonus = false) {
    const layoutConfig = this.viewModel.layoutConfig;

    const cellSz = (layoutConfig.boardWidth - layoutConfig.cellBorder) / 9;
    const innerCellSz = cellSz - layoutConfig.cellBorder;

    const xPos = (innerCellSz + layoutConfig.cellBorder) * x - innerCellSz * 0.5;
    const yPos = (innerCellSz + layoutConfig.cellBorder) * y - innerCellSz * 0.5;

    const xScaled = (layoutConfig.hMargin + xPos) * this.currentScaleFactor + this.scaledOffsetX;
    const yScaled = (layoutConfig.vMargin + yPos) * this.currentScaleFactor;

    const fontSize = this.textResizer.fitText('XXXX', cellSz * 3 * this.currentScaleFactor, 14);
    const fontScaler = score < 100 || isBonus ? 1 : 1.5;

    const pointsLabel = this.scene.add
      .text(xScaled, yScaled, `${score}`, {
        fontFamily: this.viewModel.defaultFontFamily,
        fontStyle: 'bold',
        color: this.viewModel.pointsLabelFill,
      })
      .setFontSize(fontSize * fontScaler)
      .setOrigin(0.5)
      .setDepth(500)
      .setScale(0.5) // Scale from 0.5 to 1.0 to keep the font sharp
      .setAlpha(0)
      .setStroke(this.viewModel.pointsLabelStroke, 15);

    const targetY = isBonus ? yScaled - innerCellSz * 3 : yScaled;

    this.scene.tweens.add({
      targets: pointsLabel,
      alpha: 1.0,
      scale: 1.0,
      duration: 500,
      ease: 'Quart.Out',
      onComplete: () => {
        this.scene.tweens.add({
          targets: pointsLabel,
          alpha: 0,
          y: targetY,
          scale: 0.8,
          duration: 150,
          ease: 'Quart.In',
          onComplete: () => {
            pointsLabel.destroy();
          },
        });
      },
    });
  }
}

function calcAverage(nums: number[]): number {
  if (nums.length === 0) {
    return 4;
  }

  if (nums.length % 2 === 0) {
    const mid1 = nums[nums.length / 2 - 1];
    const mid2 = nums[nums.length / 2];
    return (mid1 + mid2) / 2;
  } else {
    // For an odd-sized list, return the single middle element
    return nums[Math.floor(nums.length / 2)];
  }
}
